import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import useMessageTemplatesList from "#/message-templates/hooks/useMessageTemplatesList";
import useDebouncedValue from "#/message-templates/hooks/useDebouncedValue";
import MessageTemplatesTable, {
  MessageTemplatesEmptyState,
} from "#/message-templates/components/MessageTemplatesTable";
import MessageTemplatesTableSkeleton from "#/message-templates/components/MessageTemplatesTableSkeleton";
import MessageTemplatesPagination from "#/message-templates/components/MessageTemplatesPagination";
import { MessageTemplateListItem } from "#/services/messageTemplates";
import OutreachSettingsPanel from "#/admin/outreach-settings/components/OutreachSettingsPanel";

const PAGE_SIZE = 25;

function parsePage(search: string) {
  const params = new URLSearchParams(search);
  const raw = params.get("page");
  const page = raw ? Number(raw) : 1;
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function setPageParam(search: string, page: number) {
  const params = new URLSearchParams(search);
  if (page <= 1) {
    params.delete("page");
  } else {
    params.set("page", String(page));
  }
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function MessageTemplatesLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = useMemo(() => parsePage(location.search), [location.search]);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 250);

  const offset = (currentPage - 1) * PAGE_SIZE;

  const listQuery = useMessageTemplatesList({
    limit: PAGE_SIZE,
    offset,
    q: debouncedSearch || undefined,
  });

  const templates = useMemo(() => {
    const items = Array.isArray(listQuery.data) ? listQuery.data : [];

    const filtered = debouncedSearch
      ? items.filter((item) => {
          const haystack = [
            item.title,
            item.key,
            item.name,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(debouncedSearch.toLowerCase());
        })
      : items;

    const sorted = [...filtered].sort((a, b) => {
      const aDate = new Date(a.updated_at || a.created_at || 0).getTime();
      const bDate = new Date(b.updated_at || b.created_at || 0).getTime();
      return bDate - aDate;
    });

    return sorted as MessageTemplateListItem[];
  }, [debouncedSearch, listQuery.data]);

  const contextKey = useMemo(
    () => `${currentPage}-${debouncedSearch}`,
    [currentPage, debouncedSearch]
  );

  const hasNextPage = templates.length === PAGE_SIZE;

  const updatePage = useCallback(
    (nextPage: number) => {
      navigate({
        pathname: location.pathname,
        search: setPageParam(location.search, nextPage),
      });
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (currentPage !== 1) {
      updatePage(1);
    }
  }, [currentPage, debouncedSearch, updatePage]);

  return (
    <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2">
          <Typography variant="h2" className="text-brown-900">
            Message Templates
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            Review and search message templates used by the admin team.
          </Typography>
        </div>
        <ActionButton onClick={() => navigate("/admin/templates/new")}>
          New draft template
        </ActionButton>
      </div>

      <OutreachSettingsPanel />

      <div className="flex flex-col gap-4 rounded-xl border border-beige-200 bg-white p-4 shadow-sm">
        <Input
          id="message-template-search"
          label="Search"
          placeholder="Search templates"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />

        {listQuery.isLoading ? (
          <MessageTemplatesTableSkeleton />
        ) : listQuery.isError ? (
          <div className="rounded-xl border border-terracotta/30 bg-cream-50 p-6">
            <Typography variant="h4" className="text-terracotta">
              Unable to load message templates
            </Typography>
            <Typography variant="body2" className="text-brown-700">
              {listQuery.error instanceof Error
                ? listQuery.error.message
                : "Please try again."}
            </Typography>
            <div className="mt-4">
              <ActionButton onClick={() => listQuery.refetch()}>
                Retry
              </ActionButton>
            </div>
          </div>
        ) : templates.length === 0 ? (
          <MessageTemplatesEmptyState />
        ) : (
          <MessageTemplatesTable items={templates} contextKey={contextKey} />
        )}
      </div>

      {!listQuery.isLoading && !listQuery.isError && templates.length > 0 ? (
        <MessageTemplatesPagination
          page={currentPage}
          hasNext={hasNextPage}
          onPrevious={() => updatePage(Math.max(1, currentPage - 1))}
          onNext={() => updatePage(currentPage + 1)}
          isLoading={listQuery.isFetching}
        />
      ) : null}
    </div>
  );
}

export default MessageTemplatesLayout;
